import type { BoardColumn } from '../interfaces/BoardDetails';

export const MIN_BOARD_COLUMNS = 2;
export const MAX_BOARD_COLUMNS = 6;
export const DEFAULT_BOARD_COLUMN_COUNT = 3;
export const MAX_BOARD_COLUMN_TITLE_LENGTH = 60;

export const clampBoardColumnCount = (count: number) => {
  if (!Number.isFinite(count)) {
    return DEFAULT_BOARD_COLUMN_COUNT;
  }

  return Math.min(MAX_BOARD_COLUMNS, Math.max(MIN_BOARD_COLUMNS, Math.round(count)));
};

export const createDefaultBoardColumns = (count = DEFAULT_BOARD_COLUMN_COUNT): BoardColumn[] => {
  const columnCount = clampBoardColumnCount(count);

  return Array.from({ length: columnCount }, (_value, index) => ({
    id: `column-${index + 1}`,
    title: `Column ${index + 1}`,
    sortNo: index + 1,
    allowCardCreation: true,
  }));
};

export const sanitizeBoardColumnTitle = (title: string) =>
  title.trim().slice(0, MAX_BOARD_COLUMN_TITLE_LENGTH);

export const normalizeBoardColumns = (
  columns: BoardColumn[] | undefined,
  fallbackCount = DEFAULT_BOARD_COLUMN_COUNT
): BoardColumn[] => {
  const normalizedColumns = Array.isArray(columns)
    ? columns
        .map((column, index) => {
          const id = typeof column.id === 'string' ? column.id.trim() : '';
          const title =
            typeof column.title === 'string' ? sanitizeBoardColumnTitle(column.title) : '';

          return {
            id: id || `column-${index + 1}`,
            title: title || `Column ${index + 1}`,
            sortNo: typeof column.sortNo === 'number' ? column.sortNo : index + 1,
            allowCardCreation: column.allowCardCreation !== false,
          };
        })
        .sort((left, right) => left.sortNo - right.sortNo)
    : [];

  return normalizedColumns.length >= MIN_BOARD_COLUMNS
    ? normalizedColumns.map((column, index) => ({ ...column, sortNo: index + 1 }))
    : createDefaultBoardColumns(fallbackCount);
};

export const resizeBoardColumns = (
  columns: BoardColumn[],
  nextCount: number,
  previousCount = columns.length
) => {
  const columnCount = clampBoardColumnCount(nextCount);
  const normalizedColumns = normalizeBoardColumns(columns, previousCount);

  if (columnCount <= normalizedColumns.length) {
    return normalizedColumns.slice(0, columnCount).map((column, index) => ({
      ...column,
      sortNo: index + 1,
    }));
  }

  const defaultColumns = createDefaultBoardColumns(columnCount);

  return [...normalizedColumns, ...defaultColumns.slice(normalizedColumns.length)].map(
    (column, index) => ({
      ...column,
      sortNo: index + 1,
    })
  );
};

export const canAddCardsToBoardColumn = (
  columns: BoardColumn[],
  columnId: string,
  canAddCards: boolean
) => {
  if (!canAddCards) {
    return false;
  }

  const column = columns.find((candidate) => candidate.id === columnId);
  return column?.allowCardCreation !== false;
};
